import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  WarningOutlined,
  ShopOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { dataEntryAPI, stationOwnerAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

interface Stock {
  _id: string;
  station: {
    _id: string;
    name: string;
  };
  productType: string;
  productName: string;
  quantity: number;
  unit: string;
  minStockLevel: number;
  maxStockLevel?: number;
  unitPrice: number;
  totalValue: number;
  isActive: boolean;
}

const Stocks: React.FC = () => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [lowStockItems, setLowStockItems] = useState<Stock[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStock, setEditingStock] = useState<Stock | null>(null);
  const [selectedStation, setSelectedStation] = useState<string>('all');
  const [selectedProductType, setSelectedProductType] = useState<string>('all');
  const [isMobile, setIsMobile] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchStocks();
  }, [selectedStation, selectedProductType]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchStocks = async () => {
    setLoading(true);
    try {
      const stationId = selectedStation === 'all' ? undefined : selectedStation;
      const productType = selectedProductType === 'all' ? undefined : selectedProductType;
      const response = await dataEntryAPI.getStocks(stationId, productType);
      setStocks(response.stocks || []);
      setLowStockItems(response.lowStockItems || []);
      setSummary(response.summary || null);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Stoklar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStock = () => {
    setEditingStock(null);
    form.resetFields();
    form.setFieldsValue({
      unit: 'litre',
      minStockLevel: 0
    });
    setModalVisible(true);
  };

  const handleEditStock = (stock: Stock) => {
    setEditingStock(stock);
    form.setFieldsValue({
      station: stock.station._id,
      productType: stock.productType,
      productName: stock.productName,
      quantity: stock.quantity,
      unit: stock.unit,
      minStockLevel: stock.minStockLevel,
      maxStockLevel: stock.maxStockLevel,
      unitPrice: stock.unitPrice
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        stationId: values.station,
        productType: values.productType,
        productName: values.productName,
        quantity: values.quantity || 0,
        unit: values.unit,
        minStockLevel: values.minStockLevel || 0,
        maxStockLevel: values.maxStockLevel || null,
        unitPrice: values.unitPrice || 0
      };

      if (editingStock) {
        await dataEntryAPI.updateStock(editingStock._id, data);
        message.success('Stok güncellendi');
      } else {
        await dataEntryAPI.addStock(data);
        message.success('Stok eklendi');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingStock(null);
      fetchStocks();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const productTypeLabels: any = {
    fuel: 'Yakıt',
    market: 'Market',
    car_wash: 'Oto Yıkama',
    restaurant: 'Restoran',
    other: 'Diğer'
  };

  const unitLabels: any = {
    litre: 'Litre',
    kg: 'Kg',
    adet: 'Adet',
    paket: 'Paket',
    kutu: 'Kutu'
  };

  const columns = [
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station',
    },
    {
      title: 'Ürün Tipi',
      dataIndex: 'productType',
      key: 'productType',
      render: (type: string) => (
        <Tag color="blue">{productTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Stok Miktarı',
      key: 'quantity',
      render: (record: Stock) => (
        <span style={{
          fontWeight: record.quantity <= record.minStockLevel ? 'bold' : 'normal',
          color: record.quantity <= record.minStockLevel ? '#ff4d4f' : '#000'
        }}>
          {record.quantity} {unitLabels[record.unit] || record.unit}
        </span>
      ),
    },
    {
      title: 'Min. Stok',
      dataIndex: 'minStockLevel',
      key: 'minStockLevel',
      render: (level: number, record: Stock) => (
        <span>{level} {unitLabels[record.unit] || record.unit}</span>
      ),
    },
    {
      title: 'Birim Fiyat',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (price: number) => `${price.toLocaleString('tr-TR')} ₺`,
    },
    {
      title: 'Toplam Değer',
      dataIndex: 'totalValue',
      key: 'totalValue',
      render: (value: number) => (
        <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
          {value.toLocaleString('tr-TR')} ₺
        </span>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: '24px' }}>
            <ShopOutlined /> Stok Yönetimi
          </Title>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space wrap style={{ width: '100%' }}>
              <Select
                value={selectedStation}
                onChange={setSelectedStation}
                style={{ width: '100%', maxWidth: 200 }}
              >
                <Option value="all">Tüm İstasyonlar</Option>
                {stations.map((station: any) => (
                  <Option key={station._id} value={station._id}>
                    {station.name}
                  </Option>
                ))}
              </Select>
              <Select
                value={selectedProductType}
                onChange={setSelectedProductType}
                style={{ width: '100%', maxWidth: 150 }}
              >
                <Option value="all">Tüm Tipler</Option>
                <Option value="fuel">Yakıt</Option>
                <Option value="market">Market</Option>
                <Option value="car_wash">Oto Yıkama</Option>
                <Option value="restaurant">Restoran</Option>
                <Option value="other">Diğer</Option>
              </Select>
            </Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddStock} block={isMobile}>
              Yeni Stok Ekle
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Düşük Stok Uyarısı */}
      {lowStockItems.length > 0 && (
        <Alert
          message={`${lowStockItems.length} ürünün stoku minimum seviyenin altında!`}
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Özet İstatistikler */}
      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam Ürün"
                value={summary.totalItems}
                prefix={<ShopOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Düşük Stok"
                value={summary.lowStockCount}
                prefix={<WarningOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam Değer"
                value={summary.totalValue}
                prefix={<DollarOutlined />}
                suffix="₺"
                precision={2}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card>
        <Table
          columns={columns}
          dataSource={stocks}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          size="small"
          onRow={(record) => ({
            onClick: () => handleEditStock(record),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>

      <Modal
        title={editingStock ? 'Stok Düzenle' : 'Yeni Stok Ekle'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingStock(null);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="station"
            label="İstasyon"
            rules={[{ required: true, message: 'İstasyon seçiniz' }]}
          >
            <Select placeholder="İstasyon seçin">
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="productType"
            label="Ürün Tipi"
            rules={[{ required: true, message: 'Ürün tipi seçiniz' }]}
          >
            <Select placeholder="Ürün tipi seçin">
              <Option value="fuel">Yakıt</Option>
              <Option value="market">Market</Option>
              <Option value="car_wash">Oto Yıkama</Option>
              <Option value="restaurant">Restoran</Option>
              <Option value="other">Diğer</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="productName"
            label="Ürün Adı"
            rules={[{ required: true, message: 'Ürün adı giriniz' }]}
          >
            <Input placeholder="Ürün adı" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="Stok Miktarı"
                rules={[{ required: true, message: 'Miktar giriniz' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="Miktar"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="unit"
                label="Birim"
                rules={[{ required: true, message: 'Birim seçiniz' }]}
              >
                <Select>
                  <Option value="litre">Litre</Option>
                  <Option value="kg">Kg</Option>
                  <Option value="adet">Adet</Option>
                  <Option value="paket">Paket</Option>
                  <Option value="kutu">Kutu</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="minStockLevel"
                label="Minimum Stok Seviyesi"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="Min. stok"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxStockLevel"
                label="Maksimum Stok Seviyesi"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="Max. stok (opsiyonel)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="unitPrice"
            label="Birim Fiyat (₺)"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              placeholder="Birim fiyat"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingStock ? 'Güncelle' : 'Ekle'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingStock(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Stocks;

